import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Eye, EyeOff, RotateCcw, X } from "lucide-react";
import type { HiddenProduct } from "../services/HiddenProductsService";
import { useTranslation } from "../context/LanguageContext";
import { getProductImageUrl } from "../utils/imageUtils";

interface HiddenProductsModalProps {
  open: boolean;
  products: HiddenProduct[];
  retailerLabel: string;
  restoringProductId?: string | null;
  restoringAll?: boolean;
  onClose: () => void;
  onRestore: (product: HiddenProduct) => void;
  onRestoreAll: () => void;
}

/**
 * Hidden products sheet. Rows are snapshotted when the sheet opens so a restored product
 * stays in place, dimmed with a green check, instead of jumping out of the list.
 */
export default function HiddenProductsModal(props: HiddenProductsModalProps) {
  if (!props.open) return null;
  return <HiddenProductsSheet key="open" {...props} />;
}

function HiddenProductsSheet({
  products,
  retailerLabel,
  restoringProductId,
  restoringAll = false,
  onClose,
  onRestore,
  onRestoreAll,
}: HiddenProductsModalProps) {
  const { t } = useTranslation();
  // Snapshot taken on mount (the sheet remounts on every open).
  const [rows] = useState(() => products);
  const stillHidden = new Set(products.map((product) => product.productId));
  const restoredCount = rows.filter((row) => !stillHidden.has(row.productId)).length;
  const remaining = rows.length - restoredCount;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-sm sm:items-center sm:p-4 sheet-backdrop-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="hidden-products-title"
      onMouseDown={onClose}
    >
      <div
        className="flex max-h-[82vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-gray-200 bg-white shadow-2xl dark:border-dark-700 dark:bg-dark-900 sm:rounded-3xl sheet-panel-in-sm"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4 dark:border-dark-800">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-purple-100 text-purple-700 dark:bg-purple-900/35 dark:text-purple-300">
              <EyeOff size={18} />
            </div>
            <div>
              <h2 id="hidden-products-title" className="text-lg font-bold leading-6 text-gray-950 dark:text-white">
                {t("products.hiddenProductsTitle")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500 dark:text-dark-300">
                {rows.length > 0
                  ? t("products.hiddenTapToRestore", { retailer: retailerLabel })
                  : t("products.hiddenProductsDescription", { retailer: retailerLabel })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-dark-800 dark:hover:text-white"
            aria-label={t("common.close")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/35 dark:text-emerald-400">
                <Eye size={22} />
              </div>
              <p className="mt-4 font-semibold text-gray-900 dark:text-white">
                {t("products.noHiddenProducts")}
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-dark-300">
                {t("products.noHiddenProductsDescription")}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((product) => {
                const restored = !stillHidden.has(product.productId);
                const isRestoring = !restored && (restoringAll || restoringProductId === product.productId);
                const name = product.name || product.productId;
                return (
                  <button
                    key={product.productId}
                    type="button"
                    disabled={restored || isRestoring}
                    onClick={() => onRestore(product)}
                    aria-label={restored ? t("products.restoredProductA11y", { name }) : t("products.restoreProductA11y", { name })}
                    className={`flex w-full items-center gap-3 rounded-2xl border p-2.5 text-left transition-colors ${
                      restored
                        ? "border-emerald-100 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20"
                        : "border-gray-100 bg-gray-50/70 hover:border-purple-200 hover:bg-purple-50/60 dark:border-dark-700 dark:bg-dark-800/70 dark:hover:border-purple-800 dark:hover:bg-purple-950/30"
                    } disabled:cursor-default`}
                  >
                    <div className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl bg-white dark:bg-dark-700 ${restored ? "opacity-45" : ""}`}>
                      {product.image ? (
                        <img
                          src={getProductImageUrl(product.image, "100")}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300 dark:text-dark-500">
                          <EyeOff size={18} />
                        </div>
                      )}
                    </div>
                    <div className={`min-w-0 flex-1 ${restored ? "opacity-55" : ""}`}>
                      <p className="line-clamp-2 text-sm font-semibold leading-5 text-gray-900 dark:text-white">
                        {name}
                      </p>
                      <p className={`mt-0.5 truncate text-xs ${restored ? "font-semibold text-emerald-700 dark:text-emerald-400" : "text-gray-400 dark:text-dark-400"}`}>
                        {restored ? t("products.hiddenRestored") : product.productId}
                      </p>
                    </div>
                    <span
                      aria-hidden="true"
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                        restored
                          ? "bg-emerald-500 text-white"
                          : "border border-purple-200 bg-white text-purple-700 dark:border-purple-800 dark:bg-dark-900 dark:text-purple-300"
                      }`}
                    >
                      {restored ? <Check size={15} strokeWidth={3} /> : <RotateCcw size={13} className={isRestoring ? "animate-spin" : ""} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div className="flex items-center gap-3 border-t border-gray-100 px-4 py-3 dark:border-dark-800">
            {restoredCount > 0 && (
              <span className="min-w-0 flex-1 truncate text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                {restoredCount === 1 ? t("products.restoredOne") : t("products.restoredMany", { count: restoredCount })}
              </span>
            )}
            {remaining > 0 ? (
              <button
                type="button"
                disabled={restoringAll}
                onClick={onRestoreAll}
                className={`flex h-11 items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 text-sm font-bold text-white hover:bg-purple-700 disabled:opacity-60 ${restoredCount > 0 ? "flex-none" : "w-full"}`}
              >
                <RotateCcw size={15} className={restoringAll ? "animate-spin" : ""} />
                {t("products.restoreAllCount", { count: remaining })}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="flex h-11 flex-none items-center justify-center rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-800 hover:bg-gray-200 dark:bg-dark-800 dark:text-white dark:hover:bg-dark-700"
              >
                {t("common.close")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
